<template>
    <div id="product-list-details-page">
        <pre class="debug" v-if="isDebug">{{ { canvasIds, canvases } }}</pre>
        <actions-menu>
            <actions-menu-item
                :v4="true"
                :action="() => $router.push('/optimizedcanvaslists')"
                icon="arrow-left"
                :label="trans('pages.optimized_productlistdetails.back_to_overview')" />
            <actions-menu-item
                :v4="true"
                :action="markListSawn"
                icon="check"
                :label="trans('pages.optimized_productlistdetails.mark_as_finished')" />
            <actions-menu-item
                :v4="true"
                :action="exportCsv"
                icon="file-excel-o"
                :label="trans('pages.optimized_productlistdetails.csv_export')" />
            <actions-menu-item
                :v4="true"
                :action="removeList"
                class="alert"
                icon="exclamation-circle"
                :label="trans('pages.optimized_productlistdetails.delete')" />
        </actions-menu>
        <loader v-if="exportingCsv" :message="trans('pages.optimized_productlistdetails.exporting_csv')" />
        <loader v-if="loading" />
        <template v-else>
            <div class="container row">
                <div class="col-67">
                    <div class="paintlist-title">
                        <div v-if="allComplete">
                            <h2>{{ trans('pages.optimized_productlistdetails.order_completed') }}</h2>
                        </div>
                    </div>
                </div>

                <div class="clearer" />

                <div class="box">
                    <div class="box-title">{{ trans('pages.optimized_productlistdetails.info') }}</div>

                    <div class="box-row">
                        <div class="box-25">{{ trans('pages.optimized_productlistdetails.creation_date') }}</div>
                        <div class="box-25">
                            <span v-if="optimizedProductList">
                                {{ optimizedProductList.createdAt }}
                            </span>
                        </div>

                        <div class="box-25">{{ trans('pages.optimized_productlistdetails.deadline') }}</div>
                        <div class="box-25">
                            <span v-if="optimizedProductList && optimizedProductList.deadline">
                                {{ optimizedProductList.deadline }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="box" v-for="canvas in usedCanvases">
                    <div class="box-title">{{ trans('pages.optimized_productlistdetails.canvas_details') }}</div>
                    <div class="box-row">
                        <div class="box-25">{{ trans('pages.optimized_productlistdetails.canvas_number') }}</div>
                        <div class="box-25">
                            <span v-if="optimizedProductList">
                                {{ canvas.code }}
                            </span>
                        </div>
                        <div class="box-25">{{ trans('pages.optimized_productlistdetails.canvas_type') }}</div>
                        <div class="box-25">
                            <span v-if="optimizedProductList">
                                {{ canvas.type }}
                            </span>
                        </div>
                    </div>
                    <div class="box-row">
                        <div class="box-25">{{ trans('pages.optimized_productlistdetails.color') }}</div>
                        <div class="box-25">
                            <span v-if="optimizedProductList">
                                {{ canvas.color }}
                            </span>
                        </div>
                    </div>
                    <div v-if="moreThanOneUsedCanvas" class="box-row">
                        <div class="box-25">{{ trans('pages.optimized_productlistdetails.products') }}</div>
                        <div class="box-75">
                            <div>
                                <div
                                    v-for="product in getProductsForCanvas(canvas.id)"
                                    class="productiditem link"
                                    @click="goToProductPage(product.id)">
                                    {{ product.number }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="clearer" />

                <div class="box-row urgent" v-if="optimizedProductList">
                    <ui5-checkbox
                        :disabled="updatingUrgent"
                        :text="trans('pages.optimized_productlistdetails.closed')"
                        :checked="optimizedProductList.urgent"
                        @change="
                            optimizedProductList.urgent = $event.target.checked;
                            updateUrgent();
                        "></ui5-checkbox>
                    <i
                        class="fa fa-question-circle"
                        @mouseover="$refs.tooltipUrgent.showAt($event.target)"
                        @mouseout="$refs.tooltipUrgent.close()" />
                    <ui5-popover
                        ref="tooltipUrgent"
                        :allow-target-overlap="false"
                        placement-type="Bottom">
                        <div>
                            {{ trans('pages.optimized_productlistdetails.closed_tooltip') }}
                        </div>
                    </ui5-popover>
                </div>
            </div>

            <h4>{{ trans('pages.optimized_productlistdetails.products_in_package') }}</h4>
            <div v-if="optimizedProductList && optimizedProductList.products">
                <div
                    class="productiditem link"
                    v-for="product in optimizedProductList.products"
                    :key="'product.' + product.number"
                    @click="goToProductPage(product.id)">
                    {{ product.number }}
                </div>
            </div>

            <div class="clearer" />

            <br />
            <div
                class="product-table"
                v-if="optimizedProductList && (!optimizedProductList.products || optimizedProductList.products.length == 0)">
                <li>{{ trans('pages.optimized_productlistdetails.no_products') }}</li>
            </div>
            <table cellspacing="0" cellpadding="0" id="pleatinglistdetails-profile-overview" class="product-table">
                <thead>
                    <tr class="product-table-header-tr">
                        <th class="product-table-header-td" />
                        <th class="product-table-header-td image-column">
                            {{ trans('pages.optimized_productlistdetails.image') }}
                        </th>
                        <th class="product-table-header-td" align="left">
                            {{ trans('pages.optimized_productlistdetails.description') }}
                        </th>
                        <th class="product-table-header-td" align="center">
                            {{ trans('pages.optimized_productlistdetails.finished') }}
                        </th>
                    </tr>
                </thead>
                <tbody
                    v-if="
                        optimizedProductList && optimizedProductList.products
                    ">
                    <template
                        v-for="[
                            product,
                            records,
                        ] in optimizedProductList.products.map((x) => [
                            x,
                            recordDict[x.id],
                        ])">
                        <tr
                            :key="'listProduct.' + product.number"
                            class="product-table-item-tr">
                            <td class="ball-column">
                                <div
                                    class="bol"
                                    :style="{
                                        backgroundColor: records.every(
                                            (record) => record.sawn == 1
                                        )
                                            ? 'green'
                                            : 'red',
                                    }" />
                            </td>
                            <td
                                align="center"
                                class="image-column"
                                valign="center">
                                <img
                                    class="pleatinglistProductImage"
                                    @click="selectedImageProduct = product"
                                    :src="product.imageSrc"
                                    :alt="product.number" />
                            </td>
                            <td align="left">
                                <div class="paintlistbold">
                                    {{ product.number }}
                                </div>
                                <div class="paintlistitalic">
                                    {{ product.reference }}
                                </div>
                                <div class="paintlistitalic">
                                    {{ product.color.ralNumber
                                    }}{{ product.color.ralType }}
                                </div>
                                <br />
                                <template v-for="record in records">
                                    <template
                                        v-for="firstSawline in (
                                            record.sawlist || []
                                        ).slice(0, 1)">
                                        <div
                                            class="paintlistdefault"
                                            :key="'sawline.record.' + record.id"
                                            :data-record-id="record.id">
                                            {{ firstSawline.name.trim() }}:
                                            {{ firstSawline.value }}
                                        </div>
                                    </template>
                                </template>
                            </td>
                            <td align="center">
                                <input
                                    type="checkbox"
                                    :checked="
                                        records.every(
                                            (record) => record.sawn == 1
                                        )
                                    "
                                    @change="onFinishToggle($event, records)" />
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </template>
        <image-modal
            v-if="selectedImageProduct"
            :source="selectedImageProduct.imageSrc"
            @modal-click="selectedImageProduct = null"
            :title="selectedImageProduct.number" />
    </div>
</template>
<script>
import ActionsMenu from '../components/ActionsMenu';
import ActionsMenuItem from '../components/ActionsMenuItem';
import optimizedProductListApi from '../api/optimizedProductList';
import Loader from '../components/loader/Loader';
import IsDebugMixin from '../mixins/is-debug';
import IdParamMixin from '../mixins/id-param';
import NavigationFunctionsMixin from '../mixins/navigation-functions';
import {
    showConfirmDialog,
    showInfoMessage,
    showErrorMessage,
} from '../dialogs';
import ImageModal from '../components/modals/ImageModal';
import canvasApi from '../api/canvas';
import localization from '../mixins/localization';

export default {
    components: {
        ImageModal,
        Loader,
        ActionsMenu,
        ActionsMenuItem,
    },
    mixins: [IsDebugMixin, IdParamMixin(), NavigationFunctionsMixin, localization],
    watch: {
        canvasIds() {
            this.canvasIds
                .filter((id) => !this.canvases.map((c) => c.ID).includes(id))
                .forEach((id) => {
                    canvasApi.fetch(id).then((response) => {
                        this.canvases.push(response.data);
                    });
                });
        },
    },
    computed: {
        usedCanvases() {
            return this.canvases.filter((x) => this.canvasIds.includes(x.id));
        },
        moreThanOneUsedCanvas() {
            return this.usedCanvases.length > 1;
        },
        canvasIds() {
            if (this.optimizedProductList == null) {
                return [];
            }
            return _.uniq(
                (this.optimizedProductList.products ?? []).map(
                    (x) => x.canvasId
                )
            );
        },
        allComplete() {
            if (!this.optimizedProductList) {
                return false;
            }

            const allComplete = (
                this.optimizedProductList.products || []
            ).every((product) => {
                const records = this.recordDict[product.id];
                return records.every((record) => record.sawn == 1);
            });

            if (allComplete) {
                window.scrollTo(0, 0);
            }

            return allComplete;
        },
    },
    data() {
        return {
            exportingCsv: false,
            loading: true,
            optimizedProductList: null,
            recordDict: null,
            selectedImageProduct: null,
            canvases: [],
            updatingUrgent: false,
        };
    },
    methods: {
        updateUrgent() {
            this.updatingUrgent = true;
            optimizedProductListApi
                .patch(this.optimizedProductList.number, {
                    urgent: this.optimizedProductList.urgent,
                })
                .catch(() => {
                    showInfoMessage(
                        this.trans('pages.optimized_productlistdetails.error_saving_closed_field')
                    );
                    this.urgent = !this.urgent;
                })
                .finally(() => {
                    this.updatingUrgent = false;
                });
        },
        exportCsv() {
            this.exportingCsv = true;
            optimizedProductListApi
                .fetchExport(this.optimizedProductList.number)
                .then((response) => {
                    const data = response.data;
                    const filename =
                        response.headers['content-disposition'].split('=')[1];
                    const blob = new Blob([data], {
                        type: response.headers['content-type'],
                    });
                    saveAs(blob, filename);
                })
                .catch(() => {
                    showErrorMessage(this.trans('pages.optimized_productlistdetails.error_exporting'));
                })
                .finally(() => {
                    this.exportingCsv = false;
                });
        },
        getProductsForCanvas(canvasId) {
            if (this.optimizedProductList == null) {
                return [];
            }
            return (this.optimizedProductList.products ?? []).filter(
                (x) => x.canvasId == canvasId
            );
        },
        markListSawn() {
            showInfoMessage(
                this.trans('pages.optimized_productlistdetails.status_update_message')
            );
            optimizedProductListApi
                .updateSawn(
                    this.idParam,
                    this.$store.getters['currentUser/getUserId']
                )
                .then(() => {
                    this.$router.push('/optimizedcanvaslists');
                })
                .catch(() => {
                    showErrorMessage(this.trans('pages.optimized_productlistdetails.unknown_error'));
                });
        },
        onFinishToggle(e, records) {
            e.target.disabled = true;
            const sawn = e.target.checked;
            const promises = records.map((record) => {
                return optimizedProductListApi
                    .updateSawnForRecord(
                        parseInt(record.id),
                        sawn,
                        this.$store.getters['currentUser/getUserId']
                    )
                    .then(() => {
                        record.sawn = sawn ? 1 : 0;
                    });
            });
            Promise.all(promises)
                .catch(() => {
                    showErrorMessage(this.trans('pages.optimized_productlistdetails.unknown_error'));
                })
                .finally(() => {
                    e.target.disabled = false;
                });
        },
        createRecordDict() {
            const recordDict = {};
            this.optimizedProductList.profiles.forEach((record) => {
                record.products.forEach((number) => {
                    if (!recordDict[number]) {
                        recordDict[number] = [];
                    }
                    recordDict[number].push(record);
                });
            });
            return recordDict;
        },
        load() {
            this.loading = true;
            optimizedProductListApi
                .fetchDetails(this.idParam, true)
                .then((response) => {
                    const optimizedProductList = response.data;
                    this.$store.commit(
                        'general/setPageTitle',
                        this.trans('pages.optimized_productlistdetails.order_number') + optimizedProductList.number,
                        { root: true }
                    );
                    optimizedProductList.products.forEach((product) => {
                        product.imageSrc =
                            Config.url +
                            '/product/customImage/?productId=' +
                            product.number +
                            '&random=' +
                            Math.random();
                    });
                    this.optimizedProductList = optimizedProductList;
                    this.recordDict = this.createRecordDict();
                })
                .catch(() => {
                    showErrorMessage(
                        this.trans('pages.optimized_productlistdetails.error_fetching_details')
                    );
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        removeList() {
            showConfirmDialog({
                title: this.trans('pages.optimized_productlistdetails.remove_order_title'),
                content: this.trans('pages.optimized_productlistdetails.remove_order_confirmation'),
                confirm: () => {
                    optimizedProductListApi
                        .remove(this.idParam)
                        .then(() => {
                            this.$router.push('/optimizedcanvaslists');
                        })
                        .catch(() => {
                            showErrorMessage(this.trans('pages.optimized_productlistdetails.remove_order_failed'));
                        });
                },
                closeOnCancel: true,
                closeOnOk: true,
                confirmButton: this.trans('pages.optimized_productlistdetails.yes'),
                cancelButton: this.trans('pages.optimized_productlistdetails.no'),
            });
        },
    },
};
</script>
